import React, { useCallback, useEffect } from "react";
import { connect } from "react-redux";

import cn from "classnames";
import { defineMessages, useIntl } from "react-intl";

import Located from "@mapmycustomers/shared/types/base/Located";
import { EntityPin } from "@mapmycustomers/shared/types/map";
import { GeoPoint } from "@mapmycustomers/shared/types/shapes";
import { Button } from "@mapmycustomers/ui";

import LoadingDots from "@app/component/LoadingDots";
import { useMap } from "@app/component/map/FeaturedMap";
import Interactive from "@app/component/map/FeaturedMap/essentials/controls/Interactive";
import { FEATURE_DATA_PROPERTY } from "@app/component/map/FeaturedMap/utils/consts";
import ButtonType from "@app/enum/ButtonType";
import StickyBar from "@app/scene/map/components/tools/StickyBar";
import { exitPinLocationMode, updatePinLocation } from "@app/scene/map/store/actions";
import {
  getPinLocationAddress,
  getPinLocationEntityId,
  getPinLocationLocated,
  getPinLocationPoints,
  isEntityWithoutLocation,
  isPinAddressChanged,
  isPinCoordinatesChanged,
  isPinLocationLoading,
} from "@app/scene/map/store/selectors";
import { RootState } from "@app/store/rootReducer";
import { formatCoordinates } from "@app/util/formatters";
import defaultMapEntryGeometryGetter from "@app/util/map/defaultMapEntryGeometryGetter";
import { mapPinIdGetter } from "@app/util/map/idGetters";
import isValidMapEntry from "@app/util/map/isValidMapEntry";

import styles from "./PinLocationToolPanel.module.scss";

interface Props {
  address?: string;
  addressChanged: boolean;
  coordinates?: GeoPoint["coordinates"];
  coordinatesChanged: boolean;
  entityId?: string;
  isEntityMapped: boolean;
  loading: boolean;
  located?: Located;
  onExitPinLocationMode: typeof exitPinLocationMode;
  onUpdatePinLocation: typeof updatePinLocation.request;
}

const messages = defineMessages({
  cancelButton: {
    id: "map.tool.pinLocation.toolbar.cancelButton",
    defaultMessage: "Cancel",
    description: "Cancel button title for Pin Location Mode",
  },
  setLocation: {
    id: "map.tool.pinLocation.toolbar.setLocation",
    defaultMessage: "Set Location",
    description: "Set location Address button title for Pin Location Mode",
  },
  text: {
    id: "map.tool.pinLocation.toolbar.text",
    defaultMessage: "Drag and drop pin to change its location",
    description: "Pin Location Mode description",
  },
  updateButton: {
    id: "map.tool.pinLocation.toolbar.updateButton",
    defaultMessage: "Update Address",
    description: "Update Pin Address button title for Pin Location Mode",
  },
});

const PinLocationToolPanel: React.FC<Props> = ({
  address,
  addressChanged,
  coordinates,
  coordinatesChanged,
  entityId,
  isEntityMapped,
  loading,
  located,
  onExitPinLocationMode,
  onUpdatePinLocation,
}) => {
  const intl = useIntl();
  const { map } = useMap();

  useEffect(() => {
    return () => {
      onExitPinLocationMode();
    };
  }, [onExitPinLocationMode]);

  const handleUpdateAddress = useCallback(() => {
    onUpdatePinLocation({ exitMode: true });
  }, [onUpdatePinLocation]);

  const handleCancel = useCallback(() => {
    // Restore original geometry of active pin location feature to discard dragging results
    map.data.forEach((feature) => {
      const entity = feature.getProperty(FEATURE_DATA_PROPERTY) as EntityPin;
      if (entity?.data && entityId === mapPinIdGetter(entity) && isValidMapEntry(entity)) {
        feature.setGeometry(defaultMapEntryGeometryGetter(entity));
      }
    });

    onExitPinLocationMode();
  }, [entityId, map, onExitPinLocationMode]);

  const coordinatesForLiteral = located?.geoPoint?.coordinates ?? coordinates;

  return (
    <Interactive>
      <StickyBar className={styles.container}>
        <div className={styles.content}>
          <div className={styles.description}>{intl.formatMessage(messages.text)}</div>
          {loading ? (
            <LoadingDots />
          ) : (
            <>
              <div className={cn(styles.address, { [styles.highlight]: addressChanged })}>
                {located?.address ?? address}
              </div>
              <div className={cn(styles.coordinates, { [styles.highlight]: addressChanged })}>
                {coordinatesForLiteral ? formatCoordinates(coordinatesForLiteral) : null}
              </div>
            </>
          )}
        </div>

        <div className={styles.action}>
          <Button onClick={handleCancel}>{intl.formatMessage(messages.cancelButton)}</Button>

          <Button
            disabled={loading || (isEntityMapped && !(coordinatesChanged || addressChanged))}
            onClick={handleUpdateAddress}
            type={ButtonType.PRIMARY}
          >
            {isEntityMapped
              ? intl.formatMessage(messages.updateButton)
              : intl.formatMessage(messages.setLocation)}
          </Button>
        </div>
      </StickyBar>
    </Interactive>
  );
};

const mapStateToProps = (state: RootState) => ({
  address: getPinLocationAddress(state),
  addressChanged: isPinAddressChanged(state),
  coordinates: getPinLocationPoints(state),
  coordinatesChanged: isPinCoordinatesChanged(state),
  entityId: getPinLocationEntityId(state),
  isEntityMapped: !isEntityWithoutLocation(state),
  loading: isPinLocationLoading(state),
  located: getPinLocationLocated(state),
});

const mapDispatchToProps = {
  onExitPinLocationMode: exitPinLocationMode,
  onUpdatePinLocation: updatePinLocation.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(PinLocationToolPanel);
